import React, {useState} from 'react'
import {Button} from '../../../../../accountManagement/shared/Button'
import {Select} from '../../../../../accountManagement/shared/Select'
import {AddToOrderRow} from '../ProductPlanSelector.styles'
import {KCP_ANNUAL_PLAN_CODE, getKcpOptions} from '../../../utils/kcpOptions'

export function isKcpCertificationPlan(code) {
  return code === KCP_ANNUAL_PLAN_CODE
}

// The saved cart option is encoded as '<amount>-<amount>'; the first segment
// is the dollar amount that matches an option value.
const savedOptionValue = itemInOrder => {
  const optionValue = itemInOrder?.configuredOptions?.optionValue
  return optionValue ? String(optionValue).split('-')[0] : ''
}

export function KcpCertificationPanel({
  plan,
  selectedCountry,
  prodType,
  itemInOrder,
  groupPlanCodes,
  groupId,
  onAddProduct,
  onUpdateProduct,
  onClose,
}) {
  const isAlreadyOnAccount = Boolean(itemInOrder?.alreadyIncluded)

  // Mirrors options.js: once the account owns KCP, only options at or above
  // the owned amount are offered. The owned subscription is a $1 unit x
  // quantity, so its dollar amount is the cart entry's quantity (price, the
  // Recurly subtotal, is the same value); recurlyQuantity wins when set.
  const ownedAmount = isAlreadyOnAccount
    ? Number(
        itemInOrder.recurlyQuantity ??
          itemInOrder.quantity ??
          itemInOrder.price,
      )
    : null
  // An owned item with an unreadable amount offers no options rather than
  // silently dropping the floor and allowing a downgrade.
  const options = getKcpOptions(selectedCountry).filter(option =>
    isAlreadyOnAccount
      ? Number.isFinite(ownedAmount) && option.price >= ownedAmount
      : true,
  )

  const [selectedValue, setSelectedValue] = useState(
    isAlreadyOnAccount ? '' : savedOptionValue(itemInOrder),
  )
  const selectedOption = options.find(o => o.value === selectedValue) ?? null

  const handleAddToOrder = () => {
    if (!selectedOption) return

    const product = {
      productId: plan.code,
      name: plan.name,
      price: selectedOption.price,
      productType: prodType === 'Center' ? 'CENTER' : 'APP',
      billingFrequency:
        plan.productBillingType === 'Annual' ? 'annual' : 'monthly',
      quantity: 1,
      // '<qty>-<price>': Recurly quantity equals the dollar amount ($1 unit).
      configuredOptions: {
        optionValue: `${selectedOption.price}-${selectedOption.price}`,
      },
      excludes: plan.excludes ?? [],
      autoAddCenters: plan.autoAddCenters ?? [],
      disableApps: plan.disableApps ?? [],
      disableCenters: plan.disableCenters ?? [],
      maxQty: plan.maxQty ?? null,
      productRank: plan.productRank ?? null,
      downgrades: plan.downgrades ?? [],
      parentProduct: plan.parentProduct,
    }

    // handleAddProduct ignores a code that is already in the cart, so an
    // owned or already-added KCP is replaced through the update path.
    if (itemInOrder && onUpdateProduct) {
      onUpdateProduct(product)
    } else {
      onAddProduct(product, groupPlanCodes, groupId)
    }
    onClose()
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
        // PanelWrapper (and the catalog card) clip overflow, so the dropdown
        // list can only show inside the panel's own height. Reserve room for
        // the field (~80px) plus one row (~36px) per option, capped at the
        // list's 240px max-height, so the whole list is visible when open.
        minHeight: `${80 + Math.min(options.length * 36, 240) + 16}px`,
      }}
    >
      <Select
        label="Price option"
        placeholder="Select price option"
        variant="form"
        size="md"
        required
        options={options.map(({value, label}) => ({value, label}))}
        value={selectedValue}
        onChange={e => setSelectedValue(e.target.value)}
      />
      <AddToOrderRow>
        <Button
          type="button"
          variant="action-orange"
          size="sm"
          disabled={!selectedOption}
          onClick={handleAddToOrder}
        >
          {itemInOrder ? 'Update Order' : 'Add To Order'}
        </Button>
      </AddToOrderRow>
    </div>
  )
}
